/**
 * <ec-product-gallery>
 *
 * Light-DOM gallery Web Component for the PDP.
 *
 * Desktop (≥ 768px):
 *   - thumbnails column on the left, main image on the right
 *   - clicking a thumb swaps the main image
 *   - the column is a vertical carousel bounded by the hero image: it
 *     scrolls, and signals it with a fade proportional to the thumbs still
 *     hidden, plus a cue button on the same ramp that scrolls one thumb
 *     down. The button sits under the scroller, in a band carved out of
 *     it, so the column stays bounded by the hero image (the home tranches
 *     share the fade arithmetic, but their cue stays a passive hint — see
 *     assets/ec-product.css)
 *
 * Mobile (< 768px):
 *   - the thumbnails column is hidden by CSS
 *   - the strip is shown as a horizontal scroll-snap carousel
 *   - the current slide, tracked by IntersectionObserver, is reflected by
 *     dots or — past the media count where a dot row stops fitting the
 *     viewport — by a `3 / 53` counter framed by prev/next steps. Liquid
 *     renders one or the other; this component just updates whichever it
 *     finds. Dots and steps both go through #setActive, so every pointer
 *     affordance shares the one scroll path.
 *
 * The component is the single source of truth for "which media is shown":
 * it listens to `ec:variant:update` and realigns itself, so the zoom, the
 * thumbs and the mobile strip cannot drift from the hero image.
 *
 * Zoom button dispatches `ec:zoom:open` (handled by ec-product.js) so the
 * gallery stays decoupled from the dialog implementation.
 */

const DESKTOP_QUERY = '(min-width: 768px)';
/** Fallback when --ec-gallery-fade-max cannot be read (kept in sync with CSS). */
const FADE_MAX = 72;
/** Below this, the leftover is a rounding artefact, not content worth signalling. */
const FADE_EPSILON = 1;

class EcProductGallery extends HTMLElement {
  #thumbs = [];
  #thumbsCol = null;
  #thumbsScroller = null;
  #thumbsCue = null;
  #mainWrapper = null;
  #mainImg = null;
  #strip = null;
  #slides = [];
  #dots = [];
  #counter = null;
  #counterTpl = '';
  #steps = [];
  #zoomBtn = null;
  #mql = null;
  #fadeMax = FADE_MAX;
  #onMql = null;
  #onClick = null;
  #onKeydown = null;
  #onPointerEnter = null;
  #onThumbsScroll = null;
  #onVariantUpdate = null;
  #observer = null;
  #thumbsResizeObserver = null;
  #prefetched = new Set();

  connectedCallback() {
    this.#thumbs = Array.from(this.querySelectorAll('[data-ec-gallery-thumb]'));
    this.#thumbsCol = this.querySelector('[data-ec-gallery-thumbs-col]');
    this.#thumbsScroller = this.querySelector('[data-ec-gallery-thumbs]');
    this.#thumbsCue = this.querySelector('[data-ec-gallery-thumbs-cue]');
    this.#mainWrapper = this.querySelector('[data-ec-gallery-main]');
    this.#mainImg = this.querySelector('.ec-product-gallery__main-img');
    this.#strip = this.querySelector('[data-ec-gallery-strip]');
    this.#slides = Array.from(this.querySelectorAll('[data-ec-gallery-slide]'));
    this.#dots = Array.from(this.querySelectorAll('[data-ec-gallery-dot]'));
    this.#counter = this.querySelector('[data-ec-gallery-counter]');
    this.#counterTpl = (this.#counter && this.#counter.dataset.tpl) || '';
    this.#steps = Array.from(this.querySelectorAll('[data-ec-gallery-step]'));
    this.#zoomBtn = this.querySelector('[data-ec-gallery-zoom]');

    // Hydrate ARIA + roving tabindex on dynamic controls
    // (mémoire: ARIA hydratation runtime).
    this.#thumbs.forEach((btn, index) => {
      btn.setAttribute('aria-pressed', index === 0 ? 'true' : 'false');
      btn.setAttribute('tabindex', index === 0 ? '0' : '-1');
    });
    this.#dots.forEach((btn, index) => {
      btn.setAttribute('aria-current', index === 0 ? 'true' : 'false');
    });
    this.#syncSteps(0);

    this.#onClick = (event) => this.#handleClick(event);
    this.addEventListener('click', this.#onClick);

    // Prefetch the high-res zoom asset as soon as the user hints they
    // might open the dialog (hovering thumb or zoom button). The 200-500ms
    // we gain typically covers the entire HD download by the time the
    // click fires (mirrors Horizon's `handleThumbnailPointerEnter`).
    this.#onPointerEnter = (event) => this.#handlePrefetch(event);
    this.addEventListener('pointerenter', this.#onPointerEnter, true);

    // WAI-ARIA Authoring Practices: roving tabindex + arrow-key navigation
    // for the thumbnail strip (acts as a composite widget).
    this.#onKeydown = (event) => this.#handleKeydown(event);
    this.addEventListener('keydown', this.#onKeydown);

    // <ec-variant-picker> is a sibling, not an ancestor, so the update event
    // has to be caught on the document.
    this.#onVariantUpdate = (event) => this.#handleVariantUpdate(event);
    document.addEventListener('ec:variant:update', this.#onVariantUpdate);

    this.#setupThumbsScroll();

    this.#mql = window.matchMedia(DESKTOP_QUERY);
    this.#onMql = () => this.#applyLayout();
    this.#mql.addEventListener('change', this.#onMql);
    this.#applyLayout();

    // Unlocks the scroll mask: until here nothing measures the overflow, so
    // a mask would sit at whatever the stylesheet last declared.
    this.classList.add('is-ready');
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    if (this.#onKeydown) this.removeEventListener('keydown', this.#onKeydown);
    if (this.#onPointerEnter) {
      this.removeEventListener('pointerenter', this.#onPointerEnter, true);
    }
    if (this.#onVariantUpdate) {
      document.removeEventListener('ec:variant:update', this.#onVariantUpdate);
    }
    if (this.#thumbsScroller && this.#onThumbsScroll) {
      this.#thumbsScroller.removeEventListener('scroll', this.#onThumbsScroll);
    }
    if (this.#mql && this.#onMql) this.#mql.removeEventListener('change', this.#onMql);
    if (this.#observer) this.#observer.disconnect();
    if (this.#thumbsResizeObserver) this.#thumbsResizeObserver.disconnect();
  }

  /**
   * Scroll affordance for the thumbs carousel. The column is capped at the
   * hero image's height, so a long product cuts its list mid-air with
   * nothing but a scrollbar to say so. The list therefore carries a mask
   * whose fade at either edge is as long as the content still hidden there,
   * capped at --ec-gallery-fade-max, plus a cue button on the same ramp
   * sitting in a band the scroller gives up rather than one added under it:
   * both taper to nothing on arrival at an edge and stay at nothing for a
   * list that fits — measured, never derived from an image count.
   */
  #setupThumbsScroll() {
    if (!this.#thumbsScroller || !this.#thumbsCol) return;

    const declared = parseFloat(
      getComputedStyle(this.#thumbsCol).getPropertyValue('--ec-gallery-fade-max')
    );
    if (declared > 0) this.#fadeMax = declared;

    this.#onThumbsScroll = () => this.#syncThumbsFade();
    this.#thumbsScroller.addEventListener('scroll', this.#onThumbsScroll, { passive: true });

    // The column is sized by the hero image, whose height follows the
    // viewport width; lazy thumbs also land as the user scrolls. Observing
    // keeps the fade honest instead of freezing it at the initial layout.
    if ('ResizeObserver' in window) {
      this.#thumbsResizeObserver = new ResizeObserver(() => this.#syncThumbsFade());
      this.#thumbsResizeObserver.observe(this.#thumbsScroller);
    }

    this.#syncThumbsFade();
  }

  /** Runs after layout (scroll / resize callbacks), so these reads are free. */
  #syncThumbsFade() {
    const el = this.#thumbsScroller;
    const holder = this.#thumbsCol;
    if (!el || !holder) return;
    const above = el.scrollTop;
    const below = el.scrollHeight - el.clientHeight - el.scrollTop;
    // Whether the list overflows at all, measured against the column and
    // not against the scroller. The scroller's height depends on the band
    // this very answer reserves; comparing to it would let the answer feed
    // itself, and the band would appear and vanish under the user at the
    // end of travel. The column's height depends on the hero image alone.
    const overflows = el.scrollHeight > holder.clientHeight + FADE_EPSILON;

    holder.style.setProperty('--ec-gallery-fade-top', `${this.#hidden(above)}px`);
    holder.style.setProperty('--ec-gallery-fade-bottom', `${this.#hidden(below)}px`);
    holder.style.setProperty('--ec-gallery-cue', String(this.#hidden(below) / this.#fadeMax));
    holder.style.setProperty('--ec-gallery-cue-on', overflows ? '1' : '0');

    // Same threshold as the opacity ramp, so nothing pops: the button only
    // exists while there is something below to scroll to. `hidden` and not
    // opacity alone — a transparent button still takes clicks and focus.
    if (this.#thumbsCue) this.#thumbsCue.hidden = below <= FADE_EPSILON;
  }

  #hidden(distance) {
    return distance > FADE_EPSILON ? Math.min(distance, this.#fadeMax) : 0;
  }

  /**
   * Honours the OS setting for every scroll this component animates. The
   * smooth scroll is the one animation left here now that the cue no longer
   * bobs, so it is the one that has to be able to stop.
   */
  #scrollBehavior() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
  }

  /**
   * One thumb down. The step is measured between two consecutive thumbs so
   * it carries the gap without having to know it, and lands the next thumb
   * where the previous one sat rather than at some arbitrary offset.
   */
  #scrollThumbsDown() {
    const scroller = this.#thumbsScroller;
    if (!scroller) return;
    const [first, second] = this.#thumbs;
    let step = 0;
    if (first instanceof HTMLElement) {
      step =
        second instanceof HTMLElement ? second.offsetTop - first.offsetTop : first.offsetHeight;
    }
    // Degenerate measurement (thumbs not laid out yet): a screenful is a
    // safer guess than a no-op the user would read as a dead button.
    if (step <= 0) step = scroller.clientHeight;
    // scrollBy clamps at the end of travel on its own.
    scroller.scrollBy({ top: step, behavior: this.#scrollBehavior() });
  }

  /**
   * Brings a thumb back into the bounded column. Scrolls the list itself
   * rather than calling scrollIntoView: the latter also walks up to the
   * page scrollport, which would yank the page when a variant change fires
   * while the user is reading the size selector further down.
   */
  #revealThumb(index) {
    const scroller = this.#thumbsScroller;
    const thumb = this.#thumbs[index];
    if (!scroller || !(thumb instanceof HTMLElement)) return;

    const pad = this.#fadeMax;
    const top = thumb.offsetTop;
    const bottom = top + thumb.offsetHeight;
    let next = scroller.scrollTop;
    if (top - pad < scroller.scrollTop) {
      next = top - pad;
    } else if (bottom + pad > scroller.scrollTop + scroller.clientHeight) {
      next = bottom + pad - scroller.clientHeight;
    }
    if (next === scroller.scrollTop) return;
    scroller.scrollTo({ top: Math.max(0, next), behavior: this.#scrollBehavior() });
  }

  /**
   * `ec-variant-picker` writes the variant's featured image straight onto
   * the hero <img>. Left at that, everything else the gallery owns — the
   * active index, the pressed thumb, the HD url the zoom opens, the mobile
   * strip position — stayed on the previous colour. Realigning here makes
   * the gallery the one place that decides which media is current.
   */
  #handleVariantUpdate(event) {
    const featured = event && event.detail && event.detail.variant?.featured_image;
    const index = this.#indexFromUrl(featured);
    if (index === -1) return;
    this.#setActive(index, { scroll: true });
  }

  /**
   * Path of an image URL, i.e. the same asset whatever width was asked for
   * (`?width=1200` vs `?width=2000`) and whatever host served it (the CDN
   * in production, the proxy under `shopify theme dev`).
   */
  #imageKey(url) {
    if (!url) return '';
    try {
      return new URL(url, window.location.href).pathname;
    } catch {
      return String(url);
    }
  }

  /** Gallery index of the media an image URL points at, -1 when unknown. */
  #indexFromUrl(url) {
    const key = this.#imageKey(url);
    if (!key) return -1;
    return this.#thumbs.findIndex(
      (thumb) => this.#imageKey(thumb.getAttribute('data-main-src')) === key
    );
  }

  #handlePrefetch(event) {
    const target = event.target;
    if (!(target instanceof Element)) return;

    // Targeted prefetch — match the user's likely next action so we never
    // download the wrong size:
    //   - hover thumb  → main 1200w (next click swaps the hero image)
    //   - hover zoom   → HD 2000w   (next click opens the zoom dialog)
    // Hovering thumbs used to prefetch HD too, which saturated bandwidth
    // (5 thumbs × 500-800 KB in parallel) and stalled the main image.
    let urlToPrefetch = '';
    const thumb = target.closest('[data-ec-gallery-thumb]');
    if (thumb) {
      urlToPrefetch = thumb.getAttribute('data-main-src') || '';
    } else if (target.closest('[data-ec-gallery-zoom]')) {
      urlToPrefetch = (this.#mainWrapper && this.#mainWrapper.getAttribute('data-zoom-src')) || '';
    }

    if (!urlToPrefetch || this.#prefetched.has(urlToPrefetch)) return;
    this.#prefetched.add(urlToPrefetch);
    // `new Image().src = url` triggers a real GET; the response lands in
    // the HTTP cache, so the next consumer (main img swap or zoom dialog)
    // paints immediately. Cheaper than `<link rel='prefetch'>` here.
    const img = new Image();
    img.src = urlToPrefetch;
  }

  #applyLayout() {
    const isDesktop = this.#mql && this.#mql.matches;
    // Visibility is handled by CSS (display:none on the wrong viewport).
    // We only update aria-hidden so screen readers stay in sync.
    if (this.#strip) {
      this.#strip.setAttribute('aria-hidden', isDesktop ? 'true' : 'false');
    }
    const dotsList = this.querySelector('[data-ec-gallery-dots]');
    if (dotsList) {
      dotsList.setAttribute('aria-hidden', isDesktop ? 'true' : 'false');
    }

    // Slides are tappable on mobile; make them focusable only there
    // to avoid double-focus with the desktop zoom button.
    for (const slide of this.#slides) {
      if (isDesktop) {
        slide.removeAttribute('tabindex');
      } else {
        slide.setAttribute('tabindex', '0');
      }
    }

    if (this.#observer) {
      this.#observer.disconnect();
      this.#observer = null;
    }
    if (!isDesktop && this.#strip && this.#slides.length > 0) {
      this.#observer = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (!entry.isIntersecting) continue;
            const index = Number.parseInt(entry.target.getAttribute('data-index') || '0', 10);
            this.#setActive(index, { scroll: false });
          }
        },
        { root: this.#strip, threshold: 0.6 }
      );
      this.#slides.forEach((slide) => this.#observer.observe(slide));
    }

    // The thumbs column only has a box on desktop; re-measure on the way in
    // so the fade isn't stuck on the zeroes it read while it was hidden.
    this.#syncThumbsFade();
  }

  #handleClick(event) {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const thumb = target.closest('[data-ec-gallery-thumb]');
    const dot = target.closest('[data-ec-gallery-dot]');
    const zoom = target.closest('[data-ec-gallery-zoom]');

    if (zoom) {
      event.preventDefault();
      this.#openZoom();
      return;
    }

    if (target.closest('[data-ec-gallery-thumbs-cue]')) {
      event.preventDefault();
      this.#scrollThumbsDown();
      return;
    }

    const slide = target.closest('[data-ec-gallery-slide]');
    if (slide && !this.#mql?.matches && !target.closest('button, a')) {
      event.preventDefault();
      this.#openZoom();
      return;
    }
    if (thumb) {
      const index = Number.parseInt(thumb.getAttribute('data-index') || '0', 10);
      this.#setActive(index, { scroll: true });
      return;
    }
    if (dot) {
      const index = Number.parseInt(dot.getAttribute('data-index') || '0', 10);
      this.#setActive(index, { scroll: true });
      return;
    }

    // Counter mode: the steps land on the exact same entry point as a dot
    // click, so the smooth scroll-snap and the observer that follows it are
    // the ones already written — no second scroll path to keep in sync.
    const step = target.closest('[data-ec-gallery-step]');
    if (step) {
      const delta = Number.parseInt(step.getAttribute('data-ec-gallery-step') || '0', 10);
      const current = Number.parseInt(this.dataset.ecActiveIndex || '0', 10);
      const next = current + delta;
      // No wrap-around: the ends are dead ends. `disabled` already blocks
      // the click, this only covers a stale index (e.g. before hydration).
      if (next < 0 || next >= this.#slides.length) return;
      this.#setActive(next, { scroll: true });
    }
  }

  #handleKeydown(event) {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;

    const slide = target.closest('[data-ec-gallery-slide]');
    if (slide && !this.#mql?.matches && (event.key === 'Enter' || event.key === ' ')) {
      event.preventDefault();
      this.#openZoom();
      return;
    }

    const thumb = target.closest('[data-ec-gallery-thumb]');
    if (!thumb || this.#thumbs.length < 2) return;

    const currentIndex = this.#thumbs.indexOf(thumb);
    if (currentIndex === -1) return;

    let nextIndex = currentIndex;
    switch (event.key) {
      case 'ArrowDown':
      case 'ArrowRight':
        nextIndex = (currentIndex + 1) % this.#thumbs.length;
        break;
      case 'ArrowUp':
      case 'ArrowLeft':
        nextIndex = (currentIndex - 1 + this.#thumbs.length) % this.#thumbs.length;
        break;
      case 'Home':
        nextIndex = 0;
        break;
      case 'End':
        nextIndex = this.#thumbs.length - 1;
        break;
      default:
        return;
    }

    event.preventDefault();
    const nextThumb = this.#thumbs[nextIndex];
    if (!(nextThumb instanceof HTMLElement)) return;
    this.#setActive(nextIndex, { scroll: true });
    nextThumb.focus();
  }

  /**
   * Bounds for the counter's steps: no wrap-around, so each end is a dead
   * end and its step goes `disabled` — a real disabled button, which is
   * also greyed out by CSS, rather than a live control that does nothing.
   * The catch is that a disabled button drops the focus it holds, which a
   * keyboard user reaching the last slide would feel as the page going
   * blank under them; the focus is handed to the twin step, still live,
   * before the attribute lands.
   */
  #syncSteps(index) {
    const last = this.#slides.length - 1;
    for (const btn of this.#steps) {
      const delta = Number.parseInt(btn.getAttribute('data-ec-gallery-step') || '0', 10);
      const target = index + delta;
      const blocked = target < 0 || target > last;
      if (blocked && btn === document.activeElement) {
        const twin = this.#steps.find((other) => other !== btn);
        // preventScroll: the page must not jump while the strip is
        // already animating towards the slide that triggered this.
        if (twin instanceof HTMLElement) twin.focus({ preventScroll: true });
      }
      btn.disabled = blocked;
    }
  }

  #setActive(index, { scroll }) {
    this.dataset.ecActiveIndex = String(index);

    this.#thumbs.forEach((btn, i) => {
      btn.setAttribute('aria-pressed', i === index ? 'true' : 'false');
      btn.setAttribute('tabindex', i === index ? '0' : '-1');
    });
    this.#dots.forEach((btn, i) => {
      btn.setAttribute('aria-current', i === index ? 'true' : 'false');
    });
    // Counter replaces the dots past the count where a dot row fits; only
    // one of the two is in the DOM, so both branches run unconditionally.
    if (this.#counter && this.#counterTpl) {
      this.#counter.textContent = this.#counterTpl.replace('%n%', String(index + 1));
    }
    this.#syncSteps(index);

    // Sync main image (desktop). Prefer the thumb's `data-main-src` —
    // it's the canonical 1200w URL pre-rendered server-side, and it
    // matches what hover prefetch downloaded a moment ago, so the swap
    // is a cache hit. Fall back to the thumb img's own src if missing.
    if (this.#mainImg instanceof HTMLImageElement) {
      const thumb = this.#thumbs[index];
      if (thumb instanceof HTMLElement) {
        const mainSrc = thumb.getAttribute('data-main-src') || '';
        const thumbImg = thumb.querySelector('img');
        // Drop any srcset from the previous slide so the picker can't
        // re-pick a candidate that doesn't match the new src.
        this.#mainImg.removeAttribute('srcset');
        this.#mainImg.removeAttribute('sizes');
        if (mainSrc) {
          this.#mainImg.src = mainSrc;
        } else if (thumbImg instanceof HTMLImageElement) {
          this.#mainImg.src = thumbImg.dataset.fullSrc || thumbImg.src;
        }
        if (thumbImg instanceof HTMLImageElement) this.#mainImg.alt = thumbImg.alt;
      }
    }

    // Propagate the high-res zoom URL from the active slide to the main
    // wrapper so #openZoom can pick it up regardless of which thumb the
    // user clicked. Slide carries width=2000 (rendered server-side).
    if (this.#mainWrapper) {
      const slide = this.#slides[index];
      const zoomSrc = slide && slide.getAttribute('data-zoom-src');
      if (zoomSrc) this.#mainWrapper.setAttribute('data-zoom-src', zoomSrc);
    }

    if (!scroll) return;
    if (this.#mql?.matches) {
      this.#revealThumb(index);
    } else if (this.#strip) {
      const slide = this.#slides[index];
      if (slide instanceof HTMLElement) {
        slide.scrollIntoView({ behavior: 'smooth', inline: 'start', block: 'nearest' });
      }
    }
  }

  #openZoom() {
    // Progressive load (mirrors Horizon's zoom-dialog pattern):
    //   - `src` is the URL the gallery already painted (HTTP cached →
    //     paints instantly when assigned to the dialog <img>)
    //   - `highResSrc` is the width=2000 variant; the dialog handler
    //     downloads it via `new Image()` and swaps when ready
    // This avoids the "blank dialog while HD streams" perception.
    let src = '';
    let alt = '';
    let index = Number.parseInt(this.dataset.ecActiveIndex || '0', 10);

    if (this.#mainImg instanceof HTMLImageElement) {
      src = this.#mainImg.currentSrc || this.#mainImg.src;
      alt = this.#mainImg.alt;
    } else {
      const slide = this.#slides[index];
      const img = slide && slide.querySelector('img');
      if (img instanceof HTMLImageElement) {
        src = img.currentSrc || img.src;
        alt = img.alt;
      }
    }

    // The HD url is resolved from the pixels on screen, not from the stored
    // index: anything that writes the hero <img> without going through
    // #setActive would otherwise have the dialog paint the right image and
    // then swap it for the previous one's HD.
    const displayed = this.#indexFromUrl(src);
    if (displayed !== -1) index = displayed;
    const activeSlide = this.#slides[index];
    // No match (a variant poster that is not in product.images, a
    // placeholder) — better a 1200w dialog than a confident wrong HD.
    const highResSrc =
      displayed === -1 ? '' : (activeSlide && activeSlide.getAttribute('data-zoom-src')) || '';

    this.dispatchEvent(
      new CustomEvent('ec:zoom:open', {
        bubbles: true,
        detail: { src, highResSrc, alt },
      })
    );
  }
}

if (!customElements.get('ec-product-gallery')) {
  customElements.define('ec-product-gallery', EcProductGallery);
}
